import { Link } from 'react-router-dom'
import type { ContainerItem } from '../api/types'
import { t } from '../i18n/lingua'
import { Card } from './common'
import type { Column } from './DataTable'
import { DataTable } from './DataTable'

/**
 * Containers shown in tables: the inventory page and the host's CI section
 * share the columns. The content (system packages, libraries) is the image's
 * SBOM application; its vulnerable components open the host's SBOM section.
 */

export function porteDi(c: ContainerItem): string {
  return c.ports
    .map((p) => (p.host_port ? `${p.host_port}→${p.container_port}` : String(p.container_port)) + (p.protocol && p.protocol !== 'tcp' ? `/${p.protocol}` : ''))
    .join(', ')
}

function statoBadge(stato: string) {
  const tono = stato === 'running' ? 'ok' : stato === 'exited' ? 'muted' : 'warn'
  return <span className={`badge badge--${tono}`}>{stato === 'running' ? t('in esecuzione') : stato === 'exited' ? t('fermo') : stato}</span>
}

export function colonneContainer(conHost: boolean): Column<ContainerItem>[] {
  const colonne: Column<ContainerItem>[] = []
  if (conHost) {
    colonne.push({ key: 'host', header: t('Host'), value: (c) => c.host ?? '',
      render: (c) => <Link to={`/cis/${c.host_ci_id}#container`}>{c.host}</Link> })
  }
  colonne.push(
    { key: 'name', header: t('Container'), mono: true },
    { key: 'image', header: t('Immagine'), mono: true },
    { key: 'state', header: t('Stato'), render: (c) => statoBadge(c.state) },
    { key: 'runtime', header: t('Runtime') },
    { key: 'k8s', header: t('Kubernetes'), value: (c) => (c.k8s_namespace ? `${c.k8s_namespace}/${c.k8s_pod}` : '') },
    { key: 'compose', header: t('Compose'), value: (c) => (c.compose_project ? `${c.compose_project}/${c.compose_service}` : '') },
    { key: 'ports', header: t('Porte'), value: porteDi, mono: true },
    { key: 'os', header: t('Sistema'), value: (c) => c.os },
    { key: 'components', header: t('Componenti'), value: (c) => c.content?.components ?? -1, filter: false,
      render: (c) => (c.content ? c.content.components : <small className="text-muted" title={t('Il contenuto si legge con l’opzione SBOM dell’agente, solo per i container in esecuzione.')}>—</small>) },
    { key: 'vulnerable', header: t('Vulnerabili'), value: (c) => c.content?.vulnerable ?? 0, filter: false,
      render: (c) => (c.content?.vulnerable
        ? <Link to={`/cis/${c.host_ci_id}#sbom`} className="badge badge--warn">{c.content.vulnerable}</Link> : '—') },
  )
  return colonne
}

/** The CI page section «Container» of a host. */
export function SezioneContainerCI({ dati }: { dati: { runtimes: Array<{ name: string; version: string }>; containers: ContainerItem[] } }) {
  return (
    <Card title={t('Container ({n})', { n: dati.containers.length })}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I container di questo host letti dall’agente CENSO')}
        {dati.runtimes.length > 0 && ` (${dati.runtimes.map((r) => `${r.name} ${r.version}`).join(', ')})`}
        {'. '}{t('Il contenuto (pacchetti di sistema e librerie) si legge una volta per immagine con l’opzione SBOM e sta nella sezione «Software (SBOM)».')}
      </p>
      <DataTable rows={dati.containers} rowKey={(c) => c.id} columns={colonneContainer(false)}
        initialSort={{ key: 'name' }} paginazione={{ predefinita: 25 }} />
    </Card>
  )
}
